// The restaurant's Ingredients list (docs/plans/lists-orders-style.md): the Orders tab's list
// style, about stock: what is left, low or out, what it costs, and how many dishes use it.
// Like the Menu it is read whole once and filtered here, 25 rows a page.
import { stockStatus } from '@ims/core'
import type { ProductRow } from '@ims/data'
import {
  Badge,
  Button,
  cx,
  EmptyState,
  FilterPill,
  FilterPills,
  IconTile,
  Kbd,
  Pagination,
  SearchInput,
  Segmented,
  SkeletonTable,
} from '@ims/ui'
import { CookingPot, Package, Pencil, Upload } from '@ims/ui/icons'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router'
import { useLookups } from '../components.tsx'
import { useLive, useSettings } from '../hooks.ts'
import { money, qty } from '../lib/format.ts'
import { useListKeys } from '../lib/list-keys.ts'
import { moveClick, useOpen } from '../lib/motion.ts'
import { usePaged } from '../lib/paging.ts'
import { useCan } from '../workspace.tsx'
import { ReceiveStockDialog } from './receive.tsx'

export type IngredientShow = 'all' | 'low' | 'out'

/** The header's counts: all of them, categories, low and out of stock. */
export interface IngredientCounts {
  items: number
  categories: number
  low: number
  out: number
}

interface IngredientData {
  items: ProductRow[]
  stock: Map<string, number>
  /** How many dishes' recipes use each ingredient. */
  usedIn: Map<string, number>
  suppliers: Map<string, string>
}

function useIngredients(): IngredientData | undefined {
  return useLive(
    async (repo) => {
      const [rows, recipes, levels, suppliers] = await Promise.all([
        repo.list('products', { where: { for_sale: false }, limit: 1_000_000 }),
        repo.list('product_components', { limit: 1_000_000 }),
        repo.stock.levels(),
        repo.list('suppliers', { limit: 1_000_000 }),
      ])
      const stock = new Map<string, number>()
      for (const l of levels) stock.set(l.product_id, (stock.get(l.product_id) ?? 0) + l.qty_milli)
      const dishes = new Map<string, Set<string>>()
      for (const r of recipes.rows) {
        const set = dishes.get(r.component_id) ?? new Set<string>()
        set.add(r.product_id)
        dishes.set(r.component_id, set)
      }
      return {
        items: rows.rows.filter((p) => p.kind !== 'group'),
        stock,
        usedIn: new Map([...dishes].map(([id, set]) => [id, set.size])),
        suppliers: new Map(suppliers.rows.map((s) => [s.id, s.name])),
      }
    },
    [],
    ['products', 'product_components', 'stock_movements', 'suppliers'],
  ).data
}

// One row: from 640px a row of the table; below that the same cells make a card.
const COLS =
  'sm:grid-cols-[minmax(0,1.7fr)_minmax(0,0.9fr)_minmax(0,0.7fr)_minmax(0,0.8fr)_minmax(0,0.75fr)_minmax(0,0.6fr)_10rem]'
const COLS_NO_COST =
  'sm:grid-cols-[minmax(0,1.9fr)_minmax(0,0.9fr)_minmax(0,0.7fr)_minmax(0,0.8fr)_minmax(0,0.6fr)_10rem]'
const ROW =
  'grid items-center gap-x-3 gap-y-1 max-sm:grid-cols-[minmax(0,1fr)_auto] max-sm:rounded-xl max-sm:border max-sm:border-border max-sm:bg-surface max-sm:p-3 max-sm:shadow-card sm:border-b sm:border-border/60 sm:px-4 sm:py-2'
const CELL = {
  name: 'flex min-w-0 items-center gap-2.5 max-sm:col-start-1 max-sm:row-span-2 max-sm:row-start-1',
  category: 'text-sm max-sm:hidden',
  stock: 'tabular text-right font-bold max-sm:col-start-2 max-sm:row-start-1 max-sm:self-end',
  status: 'max-sm:col-start-2 max-sm:row-start-2 max-sm:self-start max-sm:justify-self-end',
  muted: 'text-[13px] text-muted max-sm:hidden',
  actions:
    // On a phone only Receive shows here, so a row without it has no button line.
    'flex justify-end gap-1.5 max-sm:col-span-2 max-sm:row-start-3 max-sm:mt-1.5 max-sm:[&:not(:has([data-phone]))]:hidden',
}
const selectedRow = 'lg:bg-stock-wash lg:shadow-[inset_3px_0_0_var(--color-stock)]'

export function IngredientsList({
  show,
  onShow,
  search,
  onSearch,
  searchEnd,
  canEdit,
  selecting,
  selected,
  onToggle,
  onEdit,
  onAdd,
  onCounts,
}: {
  show: IngredientShow
  onShow: (show: IngredientShow) => void
  search: string
  onSearch: (value: string) => void
  /** The scanner button inside the search box. */
  searchEnd: ReactNode
  canEdit: boolean
  selecting: boolean
  selected: ReadonlySet<string>
  onToggle: (ids: string[], on: boolean) => void
  onEdit: (product: ProductRow) => void
  onAdd: () => void
  onCounts: (counts: IngredientCounts) => void
}) {
  const settings = useSettings()
  const lookups = useLookups()
  const navigate = useNavigate()
  const openItem = useOpen()
  // What an ingredient costs is for admins, as everywhere else (Profit, recipe costs).
  const seesCost = useCan('admin')
  const data = useIngredients()
  const [category, setCategory] = useState('all')
  const [picked, setPicked] = useState<string | null>(null)
  const [receiving, setReceiving] = useState<ProductRow | null>(null)

  const items = data?.items ?? []
  const left = (p: ProductRow) => data?.stock.get(p.id) ?? 0
  const state = (p: ProductRow) => (p.track_stock ? stockStatus(left(p), p.min_stock_milli) : null)
  const categoryOf = (p: ProductRow) =>
    p.category_id ? lookups.categories.find((c) => c.id === p.category_id) : undefined

  const counts: IngredientCounts = {
    items: items.length,
    categories: new Set(items.map((p) => p.category_id).filter(Boolean)).size,
    low: items.filter((p) => state(p) === 'low_stock').length,
    out: items.filter((p) => state(p) === 'out_of_stock').length,
  }
  const countsKey = JSON.stringify(counts)
  useEffect(() => {
    if (data) onCounts(JSON.parse(countsKey) as IngredientCounts)
    // `countsKey` stands for the counts; the callback is the parent's setter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countsKey, !!data])

  const term = search.trim().toLowerCase()
  const matches = (p: ProductRow) =>
    !term ||
    [p.name, p.sku, p.barcode, p.tags, lookups.brand(p.brand_id)?.name].some((v) =>
      v?.toLowerCase().includes(term),
    )
  const inView = items.filter(
    (p) =>
      matches(p) &&
      (show === 'low'
        ? state(p) === 'low_stock'
        : show === 'out'
          ? state(p) === 'out_of_stock'
          : true),
  )
  // Sections by their order, then by name; ingredients with no category come last.
  const sections = lookups.categories
    .filter((c) => inView.some((p) => p.category_id === c.id))
    .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name))
  const rank = new Map(sections.map((c, i) => [c.id, i]))
  const shown = inView
    .filter((p) => category === 'all' || p.category_id === category)
    .sort(
      (a, b) =>
        (rank.get(a.category_id ?? '') ?? sections.length) -
          (rank.get(b.category_id ?? '') ?? sections.length) || a.name.localeCompare(b.name),
    )
  const paged = usePaged(shown, JSON.stringify([show, category, term]))
  const pageRows = paged.rows
  const selectedItem = pageRows.find((p) => p.id === picked) ?? pageRows[0]
  const canReceive = (p: ProductRow) =>
    canEdit && (state(p) === 'low_stock' || state(p) === 'out_of_stock')

  const rows = useRef<HTMLUListElement>(null)
  useListKeys({
    ids: pageRows.map((p) => p.id),
    active: selectedItem?.id ?? null,
    setActive: setPicked,
    page: paged,
    list: rows,
    open: () => selectedItem && openItem(`/products/${selectedItem.id}`),
    keys: {
      e: () => canEdit && selectedItem && onEdit(selectedItem),
      r: () => selectedItem && canReceive(selectedItem) && setReceiving(selectedItem),
    },
  })

  const status = (p: ProductRow): ReactNode => {
    const s = state(p)
    if (!p.is_active) return <Badge>Inactive</Badge>
    if (s === null) return <span className="text-[13px] text-muted max-sm:hidden">Not counted</span>
    if (s === 'out_of_stock') return <Badge tone="danger">Out of stock</Badge>
    if (s === 'low_stock') return <Badge tone="warning">Low</Badge>
    return <Badge tone="success">In stock</Badge>
  }
  const unitOf = (p: ProductRow) => lookups.unit(p.unit_id)?.symbol
  const allShownSelected = shown.length > 0 && shown.every((p) => selected.has(p.id))
  const cols = seesCost ? COLS : COLS_NO_COST

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2.5">
        <Segmented
          label="Which ingredients"
          value={show}
          onChange={(v) => {
            onShow(v)
            setCategory('all')
          }}
          options={[
            { value: 'all', label: 'All' },
            { value: 'low', label: 'Low' },
            { value: 'out', label: 'Out of stock' },
          ]}
          className="max-sm:w-full"
        />
        <SearchInput
          listKeys
          value={search}
          onChange={onSearch}
          placeholder="Name or code"
          end={searchEnd}
          className="w-full sm:ml-auto sm:w-64"
        />
      </div>
      <FilterPills label="Category">
        <FilterPill selected={category === 'all'} onSelect={() => setCategory('all')}>
          All {inView.length}
        </FilterPill>
        {sections.map((c) => (
          <FilterPill key={c.id} selected={category === c.id} onSelect={() => setCategory(c.id)}>
            {c.name} {inView.filter((p) => p.category_id === c.id).length}
          </FilterPill>
        ))}
      </FilterPills>

      {!data ? (
        <div className="rounded-card bg-surface shadow-card">
          <SkeletonTable cols={5} />
        </div>
      ) : shown.length === 0 ? (
        <div className="rounded-card bg-surface shadow-card">
          <EmptyState
            icon={<CookingPot className="size-6" />}
            title={
              items.length === 0
                ? 'No ingredients yet'
                : show === 'low' && !term
                  ? 'Nothing is low'
                  : show === 'out' && !term
                    ? 'Nothing is out of stock'
                    : 'No ingredient matches'
            }
            description={
              items.length === 0
                ? 'Add flour, oil, meat and whatever else your dishes use. A dish’s recipe then takes them out of stock.'
                : undefined
            }
            action={
              items.length === 0 &&
              canEdit && (
                <div className="flex flex-wrap justify-center gap-2">
                  <Button onClick={onAdd}>Add ingredient</Button>
                  <Button
                    variant="secondary"
                    icon={<Upload className="size-4" />}
                    onClick={() => navigate('/products/import')}
                  >
                    Import from Excel
                  </Button>
                </div>
              )
            }
          />
        </div>
      ) : (
        <section
          aria-label="Ingredients"
          className="min-w-0 sm:overflow-hidden sm:rounded-card sm:bg-surface sm:shadow-card"
        >
          <div
            aria-hidden={!selecting}
            className={cx(
              'grid items-center gap-x-3 border-b border-border px-4 py-2.5 text-table-head text-subtle max-sm:hidden',
              cols,
            )}
          >
            <span className="flex items-center gap-2.5">
              {selecting && (
                <input
                  type="checkbox"
                  aria-label="Select all shown"
                  className="size-4 accent-primary"
                  checked={allShownSelected}
                  onChange={(e) =>
                    onToggle(
                      shown.map((p) => p.id),
                      e.target.checked,
                    )
                  }
                />
              )}
              Ingredient
            </span>
            <span>Category</span>
            <span className="text-right">In stock</span>
            <span>Status</span>
            {seesCost && <span>Cost</span>}
            <span>Used in</span>
            <span />
          </div>
          <ul ref={rows} className="max-sm:space-y-2.5">
            {pageRows.map((p) => {
              const on = selectedItem?.id === p.id
              const supplier = p.supplier_id ? data.suppliers.get(p.supplier_id) : undefined
              const unit = unitOf(p)
              const used = data.usedIn.get(p.id) ?? 0
              const s = state(p)
              return (
                <li
                  key={p.id}
                  data-id={p.id}
                  aria-current={on ? 'true' : undefined}
                  onClick={() => openItem(`/products/${p.id}`)}
                  className={cx(ROW, cols, 'cursor-pointer', on ? selectedRow : 'lg:hover:bg-soft')}
                >
                  <span className={CELL.name}>
                    {selecting && (
                      <input
                        type="checkbox"
                        aria-label={`Select ${p.name}`}
                        className="size-4 shrink-0 accent-primary"
                        checked={selected.has(p.id)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => onToggle([p.id], e.target.checked)}
                      />
                    )}
                    <IconTile area="stock" size="sm" className="max-sm:size-11">
                      <CookingPot className="size-4" />
                    </IconTile>
                    <span className="min-w-0">
                      <Link
                        to={`/products/${p.id}`}
                        onClick={(e) => {
                          e.stopPropagation()
                          moveClick(`/products/${p.id}`, 'open')(e)
                        }}
                        className={cx(
                          'block font-bold hover:underline focus-visible:underline max-sm:line-clamp-2 sm:truncate',
                          !p.is_active && 'text-muted',
                        )}
                      >
                        {p.name}
                      </Link>
                      <span className="block truncate text-xs text-muted sm:text-[12.5px]">
                        <span className="sm:hidden">
                          {[
                            categoryOf(p)?.name ?? 'No category',
                            used === 0 ? 'no dish' : `${used} ${used === 1 ? 'dish' : 'dishes'}`,
                          ].join(' · ')}
                        </span>
                        <span className="max-sm:hidden">
                          {supplier ? `Usual supplier: ${supplier}` : 'No usual supplier'}
                        </span>
                      </span>
                    </span>
                  </span>
                  <span className={CELL.category}>
                    {categoryOf(p)?.name ?? <span className="text-muted">No category</span>}
                  </span>
                  <span className={cx(CELL.stock, s === 'out_of_stock' && 'text-danger')}>
                    {p.track_stock ? `${qty(left(p))}${unit ? ` ${unit}` : ''}` : '—'}
                  </span>
                  <span className={CELL.status}>{status(p)}</span>
                  {seesCost && (
                    <span className={CELL.muted}>
                      {p.cost_price_paisa
                        ? `${money(p.cost_price_paisa, settings)}${unit ? ` / ${unit}` : ''}`
                        : '—'}
                    </span>
                  )}
                  <span className={CELL.muted}>
                    {used === 0 ? 'No dish' : `${used} ${used === 1 ? 'dish' : 'dishes'}`}
                  </span>
                  <span className={CELL.actions} onClick={(e) => e.stopPropagation()}>
                    {canReceive(p) && (
                      <Button
                        variant="soft"
                        size="sm"
                        data-phone
                        aria-label={`Receive ${p.name}`}
                        icon={<Package className="size-4" />}
                        className="h-[34px] px-3 text-[13px] whitespace-nowrap max-sm:h-10 max-sm:flex-1"
                        onClick={() => setReceiving(p)}
                      >
                        Receive
                      </Button>
                    )}
                    {canEdit && (
                      <Button
                        variant="secondary"
                        size="sm"
                        aria-label={`Edit ${p.name}`}
                        title="Edit"
                        className="h-[34px] w-[34px] px-0 max-sm:hidden"
                        icon={<Pencil className="size-4" />}
                        onClick={() => onEdit(p)}
                      />
                    )}
                  </span>
                </li>
              )
            })}
          </ul>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-2.5 text-xs text-muted max-sm:px-1">
            <span>Sorted by category, then name.</span>
            <span className="ml-auto flex flex-wrap items-center gap-x-3 gap-y-1 max-lg:hidden">
              <span>
                <Kbd>↑</Kbd> <Kbd>↓</Kbd> move
              </span>
              <span>
                <Kbd>Enter</Kbd> open
              </span>
              {canEdit && (
                <>
                  <span>
                    <Kbd>E</Kbd> edit
                  </span>
                  <span>
                    <Kbd>R</Kbd> receive
                  </span>
                </>
              )}
            </span>
          </div>
          <Pagination
            offset={paged.offset}
            limit={paged.limit}
            total={paged.total}
            onChange={paged.setOffset}
          />
        </section>
      )}
      {receiving && (
        <ReceiveStockDialog products={[receiving]} onClose={() => setReceiving(null)} />
      )}
    </div>
  )
}
